import { Flex } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { AiMarketerHero } from '~/features/ai-marketer/components/ai-marketer-hero';
import {
  AI_MARKETER_ANALYTICS_EVENT,
  COPY,
  FREE_COURSE_KEY,
  FREE_COURSE_SLUG,
  STAY_UP_TO_DATE_SECTION_ID,
} from '~/features/ai-marketer/constants';
import { focusStayPostedEmailAfterScroll } from '~/features/ai-marketer/utils/focus-stay-posted-email';
import { trackEvent } from '~/features/analytics/track-event';
import { CircleArrowRightIcon, CirclePlusIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useCourseLink } from '~/features/program/hooks/use-course-link';

// Sampled from the edges of hero-desktop.webp / hero-mobile.webp so the section fill and
// overlays blend into the artwork on viewports wider than the image.
const HERO_EDGE_COLOR = '#020612';

export function AiMarketerHeroSection() {
  const freeCourseLink = useCourseLink({ courseSlug: FREE_COURSE_SLUG });

  return (
    <AiMarketerHero
      backgroundColor={HERO_EDGE_COLOR}
      description={COPY.hero.description}
      heroTextMaxWidth={{ base: '100%', md: '640px' }}
      title={COPY.hero.title}
      backgroundImageOverlayGradient={{
        base: 'linear(to-t, rgba(2, 6, 18, 0.92) 0%, rgba(2, 6, 18, 0.35) 45%, rgba(2, 6, 18, 0.2) 100%)',
        md: 'linear(to-r, rgba(2, 6, 18, 0.95) 0%, rgba(2, 6, 18, 0.85) 45%, rgba(2, 6, 18, 0) 80%)',
        xl: 'radial(circle at right, rgba(2, 6, 18, 0) 0%, rgba(2, 6, 18, 0) 35%, rgba(2, 6, 18, 0.55) 100%)',
      }}
      backgroundImages={{
        mobile: {
          src: '/images/ai-marketer/hero-mobile.webp',
          objectPosition: 'center -64px',
          style: { maxWidth: '374px', margin: '0 auto' },
        },
        desktop: {
          src: '/images/ai-marketer/hero-desktop.webp',
          objectPosition: 'right center',
        },
      }}
      ctaSlot={
        <Flex flexWrap='wrap' gap={{ base: '16px', md: '24px' }} width='100%'>
          <ButtonLink
            buttonProps={{
              flex: { base: '1 1 0', md: '0 0 auto' },
              leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
              px: { base: '16px', md: '24px' },
              size: 'icon-left',
              variant: 'solid',
              colorScheme: 'blue',
              onClick: () =>
                trackEvent({
                  type: 'click',
                  name: AI_MARKETER_ANALYTICS_EVENT,
                  label: 'hero_start_free_course',
                  additionalAttributes: {
                    product_ids: [FREE_COURSE_KEY],
                  },
                }),
            }}
            linkProps={{
              href: freeCourseLink,
            }}
          >
            {COPY.hero.startFreeCourse}
          </ButtonLink>
          <ButtonLink
            buttonProps={{
              flex: { base: '1 1 0', md: '0 0 auto' },
              iconSpacing: { base: 0, md: '12px' },
              leftIcon: <CirclePlusIcon display={{ base: 'none', md: 'inline-block' }} height='18px' width='18px' />,
              px: { base: '16px', md: '24px' },
              size: 'icon-left',
              variant: 'outline',
              colorScheme: 'white',
              onClick: () => {
                trackEvent({
                  type: 'click',
                  name: AI_MARKETER_ANALYTICS_EVENT,
                  label: 'hero_join_waitlist',
                });
                focusStayPostedEmailAfterScroll();
              },
            }}
            linkProps={{
              href: `#${STAY_UP_TO_DATE_SECTION_ID}`,
            }}
          >
            {COPY.hero.joinWaitlist}
          </ButtonLink>
        </Flex>
      }
    />
  );
}
